<template>
    <div class="favorites-page">
        <v-card class="section-card" elevation="0">
            <div class="card-head">
                <div class="head-gradient head-gradient--green" />
                <div class="head-inner d-flex align-center justify-space-between">
                    <div>
                        <h2 class="form-title">บันทึกรายการโปรด</h2>
                    </div>
                </div>
            </div>

            <v-divider class="mb-4" />

            <!-- fly-dot animation -->
            <div v-if="cartAnim.active" class="fly-dot" :style="cartAnim.style" ref="flyDot">
                <v-img :src="cartAnim.image" width="48" height="48" class="rounded-circle" />
            </div>

            <!-- snackbar -->
            <v-snackbar v-model="snackbar" :timeout="2000" top right color="#198C19" elevation="2">
                <v-icon left small>mdi-check-circle</v-icon>
                เพิ่มสินค้าลงตะกร้าแล้ว
            </v-snackbar>

            <div class="section-form pt-0">
                <div v-if="loading" class="py-8 d-flex justify-center">
                    <v-progress-circular indeterminate size="32" width="3" color="#16a34a" />
                </div>

                <div v-else-if="favorites.length === 0" class="empty-wrap">
                    <div class="empty-card">
                        <v-icon size="40" color="#9ca3af" class="mb-2">mdi-heart-broken</v-icon>
                        <div class="empty-title">คุณยังไม่มีรายการโปรด</div>
                        <div class="empty-sub">เลือกซื้อสินค้าที่น่าสนใจได้ใน Marketplace</div>
                        <v-btn outlined color="#198C19" class="mt-4 rounded-lg" @click="$router.push('/marketplace')">
                            เลือกซื้อสินค้า
                        </v-btn>
                    </div>
                </div>

                <div v-else class="favorites-list">
                    <div v-for="(product, index) in favorites" :key="product.id" class="favorite-block">
                        <div class="d-flex mb-4">
                            <div class="pa-2 border rounded-xl mr-4"
                                style="border-color: #eee !important; width: 136px; height: 136px; flex-shrink: 0;">
                                <v-img :src="getProductImage(product)" aspect-ratio="1" cover class="rounded-xl">
                                    <template v-slot:placeholder>
                                        <v-row class="fill-height ma-0" align="center" justify="center">
                                            <v-progress-circular indeterminate color="#198C19" />
                                        </v-row>
                                    </template>
                                </v-img>
                            </div>
                            <div class="product-details flex-grow-1 py-1">
                                <div class="product-name font-weight-bold mb-1 text-body-1">{{ product.productName }}
                                </div>
                                <!-- <div class="d-flex align-center mb-1">
                                    <v-icon small color="amber" class="mr-1">mdi-star</v-icon>
                                    <span class="text-body-2 font-weight-bold mr-1">4.6</span>
                                    <span class="grey--text text-caption">({{ product.viewCount || 0 }} รีวิว)</span>
                                </div> -->
                                <div class="product-price success--text font-weight-bold text-h6">{{
                                    formatPrice(getCurrentPrice(product)) }} บาท</div>
                                <div class="mt-1 d-sm-none">
                                    <v-chip x-small v-if="product.stock > 0" color="success" outlined
                                        label>มีสินค้า</v-chip>
                                    <v-chip x-small v-else color="error" outlined label>สินค้าหมด</v-chip>
                                </div>
                                <div class="mt-2" v-if="product.variants && product.variants.length > 0">
                                    <v-chip-group v-model="product.selectedVariantIndex" mandatory
                                        active-class="success--text text--darken-2" column>
                                        <v-chip v-for="(variant, vIndex) in product.variants" :key="vIndex" small filter
                                            outlined label>
                                            {{ getVariantLabel(variant) }}
                                        </v-chip>
                                    </v-chip-group>
                                </div>
                            </div>
                        </div>

                        <div class="favorite-footer d-flex justify-end align-center px-0 flex-wrap">
                            <div class="d-flex w-100 justify-end gap-2">
                                <v-btn icon @click="removeFavorite(product.id)">
                                    <v-icon color="pink">mdi-heart</v-icon>
                                </v-btn>
                                <div class="d-flex gap-2">
                                    <v-btn outlined color="info" class="btn-action rounded-lg px-4 text-none"
                                        height="40" @click="goToDetail(product.id)">
                                        ดูรายละเอียด
                                    </v-btn>
                                    <v-btn :ref="`cartBtn_${product.id}`" outlined color="#198C19"
                                        class="btn-action rounded-lg px-6 text-none" height="40"
                                        @click="addToCart(product, `cartBtn_${product.id}`)"
                                        :disabled="product.stock <= 0">
                                        <v-icon left>mdi-cart-plus</v-icon> เพิ่มเข้ารถเข็น
                                    </v-btn>
                                </div>
                            </div>
                        </div>

                        <v-divider v-if="index < favorites.length - 1" class="my-6" />
                    </div>
                </div>
            </div>
        </v-card>
    </div>
</template>

<script>
import { getProductImage, getImageUrl } from "@/utils/image";
import { getFavoritesApi, toggleFavoriteApi } from "@/services/api/favorite";
import { addItemToCartApi } from "@/services/api/cart";
import swalAlert from "@/services/alert/swalServices";

export default {
    name: "Favorites",
    data() {
        return {
            favorites: [],
            loading: false,
            cartAnim: { active: false, style: {}, image: "" },
            snackbar: false,
        };
    },
    async mounted() {
        await this.loadFavorites();
    },
    methods: {
        async loadFavorites() {
            this.loading = true;
            try {
                const res = await getFavoritesApi();
                if (res?.data?.success) {
                    this.favorites = res.data.data.map(product => ({
                        ...product,
                        selectedVariantIndex: product.variants && product.variants.length > 0 ? 0 : null
                    }));
                }
            } catch (e) {
                console.error("Load favorites error:", e);
            } finally {
                this.loading = false;
            }
        },

        async removeFavorite(productId) {
            if (!productId) return;
            const res = await swalAlert.Confirm({
                title: "ยืนยันการลบ",
                message: "คุณต้องการลบสินค้านี้ออกจากรายการโปรดใช่หรือไม่?",
                confirmText: "ลบ",
                cancelText: "ยกเลิก"
            });
            if (!res.isConfirmed) return;
            try {
                const r = await toggleFavoriteApi(productId);
                if (r?.data?.success) {
                    swalAlert.Success("ลบออกจากรายการโปรดแล้ว");
                    this.favorites = this.favorites.filter(p => p.id !== productId);
                }
            } catch (e) {
                console.error("Remove favorite error:", e);
                swalAlert.Error("เกิดข้อผิดพลาด", "ไม่สามารถลบรายการได้");
            }
        },

        async addToCart(product, btnRef) {
            let variantId = null;
            let price = product.price;

            if (product.variants && product.variants.length > 0 && product.selectedVariantIndex != null) {
                const variant = product.variants[product.selectedVariantIndex];
                if (variant) {
                    variantId = variant.id;
                    price = variant.price;
                    if (variant.stock <= 0) {
                        swalAlert.Warning("สินค้าหมด", "สินค้านี้หมดชั่วคราว");
                        return;
                    }
                }
            } else if (product.stock <= 0) {
                swalAlert.Warning("สินค้าหมด", "สินค้านี้หมดชั่วคราว");
                return;
            }

            try {
                const res = await addItemToCartApi({ productId: product.id, quantity: 1, price, variantId });
                if (res?.success) {
                    this.triggerFlyToCart(product, btnRef);
                } else {
                    swalAlert.Error("ไม่สามารถเพิ่มสินค้าได้", res?.message);
                }
            } catch (e) {
                console.error("Add to cart error:", e);
                swalAlert.Error("เกิดข้อผิดพลาด", "ไม่สามารถเพิ่มสินค้าลงตะกร้าได้");
            }
        },

        triggerFlyToCart(product, btnRef) {
            const cartEl = document.getElementById("nav-cart-icon");
            const btnComp = this.$refs[btnRef];
            const btn = Array.isArray(btnComp) ? btnComp[0]?.$el : btnComp?.$el || btnComp;

            // Set the thumbnail image for the dot
            this.cartAnim.image = getProductImage(product);

            if (!btn) { this.snackbar = true; return; }

            const btnRect = btn.getBoundingClientRect();
            const startX = btnRect.left + btnRect.width / 2 - 24;
            const startY = btnRect.top + btnRect.height / 2 - 24;

            let endDX = window.innerWidth - 80 - startX;
            let endDY = -(startY - 16);

            if (cartEl) {
                const r = cartEl.getBoundingClientRect();
                endDX = (r.left + r.width / 2 - 24) - startX;
                endDY = (r.top + r.height / 2 - 24) - startY;
            }

            this.cartAnim.style = { left: `${startX}px`, top: `${startY}px` };
            this.cartAnim.active = true;

            this.$nextTick(() => {
                const dotEl = this.$el.querySelector(".fly-dot");
                if (!dotEl) { this.snackbar = true; return; }

                const anim = dotEl.animate(
                    [
                        { transform: "translate(0,0) scale(1)", opacity: 1 },
                        { transform: `translate(${endDX * 0.75}px,${endDY * 0.75}px) scale(0.5)`, opacity: 0.85, offset: 0.7 },
                        { transform: `translate(${endDX}px,${endDY}px) scale(0.1)`, opacity: 0 },
                    ],
                    { duration: 680, easing: "cubic-bezier(0.4,0,0.2,1)", fill: "forwards" }
                );

                anim.onfinish = () => {
                    this.cartAnim.active = false;
                    this.snackbar = true;
                };
            });
        },

        getProductImage,
        getImageUrl,
        formatPrice(price) {
            return parseFloat(price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        getCurrentPrice(product) {
            if (product.variants && product.variants.length > 0 && product.selectedVariantIndex != null) {
                const variant = product.variants[product.selectedVariantIndex];
                if (variant) return variant.price;
            }
            return product.price;
        },
        getVariantLabel(variant) {
            if (!variant) return "";
            const parts = [];
            if (variant.color) parts.push(variant.color);
            if (variant.size) parts.push(variant.size);
            return parts.join(" ") || "ปกติ";
        },
        goToDetail(id) {
            this.$router.push({ name: 'product_detail', params: { id } });
        }
    }
};
</script>

<style scoped>
/* ─── Fly-to-cart dot ─────────────────────── */
/* No CSS animation — handled by WAAPI in JS */
.fly-dot {
    position: fixed;
    z-index: 9999;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
    pointer-events: none;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22);
}

/* ─── Card ──────────────────────────────────── */
.section-card {
    background: #fff;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    box-shadow: 0 12px 28px rgba(2, 6, 23, 0.06) !important;
}

.card-head {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    overflow: hidden;
}

.head-gradient {
    height: 76px;
    opacity: 0.18;
}

.head-gradient--green {
    background: linear-gradient(100deg, #d6230b 0%, #eed7d5 100%);
}

.head-inner {
    padding: 22px 24px 16px;
}

.form-title {
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 6px;
    font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
}

.section-form {
    padding: 22px 24px 28px;
}

.favorite-block {
    padding: 0;
}

.product-name {
    font-size: 16px;
    color: #0f172a;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.product-price {
    font-size: 18px;
}

.btn-action {
    font-weight: 600;
    font-size: 14px !important;
    letter-spacing: 0;
}

.empty-wrap {
    text-align: center;
    padding: 60px 0;
}

.empty-title {
    font-size: 18px;
    font-weight: 700;
    color: #212121;
}

.empty-sub {
    color: #757575;
}

.cursor-pointer {
    cursor: pointer;
}

@media (max-width: 600px) {
    .head-inner {
        padding: 12px 14px 12px;
    }

    .section-form {
        padding: 12px 14px 20px;
    }

    .favorite-block > .d-flex {
        gap: 10px;
    }

    .favorite-block > .d-flex > div:first-child {
        width: 88px !important;
        height: 88px !important;
    }

    .favorite-footer .d-flex.w-100 {
        flex-wrap: wrap;
        gap: 8px;
    }

    .favorite-footer .btn-action {
        flex: 1;
        min-width: 0;
        font-size: 13px !important;
    }
}
</style>
